import adminSk from "iqf-web-admin/messages/sk.json";
import { type PropsWithChildren, createContext, useContext } from "react";
import { IntlProvider, useIntl } from "react-intl";

import type { MessagesLocale } from "iqf-web-ui/locale";
import iqfSk from "iqf-web-ui/messages/sk.json";

import commonSk from "udzs-common/messages/sk.json";

import sk from "@/intl/sk.json";
import { setZodErrorMap } from "@/utils/zod/set-error-map";

const DEFAULT_LOCALE: MessagesLocale = "sk";

function mergeMessageCatalogs(...catalogs: Record<string, string>[]) {
  return catalogs.reduce<Record<string, string>>((messages, catalog) => {
    for (const [id, message] of Object.entries(catalog)) {
      messages[id] ??= message;
    }

    return messages;
  }, {});
}

type LocaleContext = {
  locale: MessagesLocale;
};

export const LocaleContext = createContext<LocaleContext | null>(null);

export function useLocaleContext() {
  const context = useContext(LocaleContext);

  if (!context) {
    throw new Error("useLocaleContext must be used within a LocaleProvider");
  }

  return context;
}

function ZodLocale() {
  const intl = useIntl();

  setZodErrorMap(intl);

  return null;
}

export function LocaleProvider({ children }: PropsWithChildren) {
  const messages = mergeMessageCatalogs(iqfSk, sk, adminSk, commonSk);

  return (
    <LocaleContext.Provider value={{ locale: DEFAULT_LOCALE }}>
      <IntlProvider
        locale={DEFAULT_LOCALE}
        messages={messages}
        // Pass your own onError handler if you want to see missing translations
        onError={() => void 0}
      >
        <ZodLocale />

        {children}
      </IntlProvider>
    </LocaleContext.Provider>
  );
}
